import React from 'react';
import { Select, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import NumberInput, {
  EnumMode,
} from '@/src/components/Antd/NumberInput/NumberInput';
import styles from './css.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import Link from '@/src/components/Link/Link';
import { Icon } from '@/src/tools/icon';
import classnames from 'classnames';

type LocalPrices = {
  [currency: string]: { [key: string]: number };
};

type FieldConfig = {
  key: string;
  label: string;
  placeholder?: string;
};

type Props = {
  value: LocalPrices;
  onChange: (value: LocalPrices) => void;
  currencyOptions: { label: string; value: string }[];
  fields: FieldConfig[];
  disabled?: boolean;
  source?: string;
};

const LocalPricingEditor: React.FC<Props> = ({
  value,
  onChange,
  currencyOptions,
  fields,
  disabled,
  source,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const currencyList = Object.entries(value);

  const handleAdd = () => {
    const available = currencyOptions
      .map((opt) => opt.value)
      .find((code) => !value[code]);
    if (!available) return;
    const emptyFieldObject = fields.reduce((acc, field) => {
      acc[field.key] = field.key === 'rrp' ? null : 0;
      return acc;
    }, {} as Record<string, number>);
    onChange({
      ...value,
      [available]: emptyFieldObject,
    });
  };

  const handleDelete = (currency: string) => {
    const copy = { ...value };
    delete copy[currency];
    onChange(copy);
  };

  const handleCurrencyChange = (prevCurrency: string, newCurrency: string) => {
    if (prevCurrency === newCurrency || value[newCurrency]) return;

    const updated: Record<string, any> = {};

    Object.entries(value).forEach(([key, val]) => {
      if (key === prevCurrency) {
        updated[newCurrency] = val;
      } else {
        updated[key] = val;
      }
    });

    onChange(updated);
  };

  const handleFieldChange = (currency: string, key: string, val: number) => {
    onChange({
      ...value,
      [currency]: {
        ...value[currency],
        [key]: val,
      },
    });
  };

  return (
    <div>
      <Nav className={styles.wrapperNav} source={source} />

      {currencyOptions.length !== currencyList.length && (
        <Button
          type='link'
          icon={<PlusOutlined />}
          onClick={handleAdd}
          className={styles.addButton}
          disabled={disabled}>
          {getI18n('general.addlocalpricing')}
        </Button>
      )}
      {currencyList.map(([currency, data]) => (
        <div key={currency} className={styles.currencyRow}>
          <div className={styles.currencyFields}>
            <div className={styles.currencyField}>
              <div className={styles.label}>
                {getI18n('general.selectCurrency')}
              </div>
              <Select
                value={currency}
                options={currencyOptions.filter(
                  (opt) => !value[opt.value] || opt.value === currency,
                )}
                onChange={(val) => handleCurrencyChange(currency, val)}
                disabled={disabled}
                style={{ width: '100%' }}
              />
            </div>

            {fields.map((field) => (
              <div key={field.key} className={styles.currencyField}>
                <div className={styles.label}>{field.label}</div>
                <NumberInput
                  disabled={disabled}
                  value={data[field.key]}
                  mode={EnumMode.money}
                  onChange={(e) => handleFieldChange(currency, field.key, e)}
                  placeholder={field.key === 'rrp' ? '' : '0.00'}
                  decimalPosition={2}
                />
              </div>
            ))}
          </div>
          <div className={styles.deleteButtonWrapper}>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(currency)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const Nav = ({ className, source }: { className: string; source?: string }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <div className={classnames(styles.nav, className)}>
      <Icon type='iconinfo2' className={styles.info} />
      <span>
        {source === 'product'
          ? getI18n('general.localpricetipproduct1')
          : getI18n('general.localpricetip1')}
        &nbsp;
      </span>
      <Link to={'/settings/currency'} className={styles.link}>
        {getI18n('general.localpricetip2')}
      </Link>
      <span>&nbsp;{getI18n('general.localpricetip3')}</span>
    </div>
  );
};

export default LocalPricingEditor;
